import { apiFetch } from './http'
import type {
  PlayerGoalkeeperDetail,
  RoleProfile,
  ChanceMatchTeam,
  PlayerHistory,
  PlayerInjury,
  CohortMetricRow,
  LeagueMatchItem,
  LeagueScope,
  LeaguePlayerPhysical,
  LeagueSetPieces,
  MatchListItem, MatchRanking, MatchReport, MatchSetPieceSheet, MatchSubphaseSheet, MatchSummary,
  MatchTransitionSheet,
  PhysicalAnalysis,
  PhysicalMatchPlayers,
  StrengthLeague, StrengthLeagues, LeagueTeamPlay,
  LeaguePhysicalLeagues, LeaguePhysicalPlayers, LeaguePlayerTotals, PlayerCompareData, PlayerListEntry, PlayerMapSquad, PlayerProfile, PlayerSearchHit,
  SeasonRankings, SeasonSetPieceSheet, SfgContacts, SfgOutcomes,
  TeamDossier, TeamListItem, TeamPhysicalAssessment, TeamSetPieces, TeamStyleProfile,
  HomeLeague, HomeTeam,
} from './types'

// Optional league scope threaded into the team dossier/style endpoints so a
// team can be analysed in a chosen competition/season (league picker).
// `fromDate` (ISO) dokłada okno „od daty", które PRZECHODZI PRZEZ GRANICĘ
// SEZONÓW (styl + predyktory + motoryka w dossier PDF: od 01.01.2026, czyli
// wiosna 25/26 + bieżący sezon). Bez niej endpointy liczą wybrany sezon.
type Scope = { competitionId?: number; seasonId?: number; fromDate?: string }

function scopeQS(opts?: Scope): string {
  const q = new URLSearchParams()
  if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
  if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
  if (opts?.fromDate !== undefined) q.set('from_date', opts.fromDate)
  const qs = q.toString()
  return qs ? `?${qs}` : ''
}

function matchIdsQS(ids: number[]): string {
  const q = new URLSearchParams()
  for (const id of ids) q.append('match_ids', String(id))
  return q.toString()
}

export async function getJSON<T>(path: string): Promise<T> {
  const res = await apiFetch(path, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} on ${path}`)
  return res.json() as Promise<T>
}

export const api = {
  // `includePending` — także mecze rozegrane, których StatsBomb jeszcze nie
  // przetworzył, o ile znamy wynik (forma w dossier). Takie mają has_statsbomb=false.
  matches: (seasonId?: number, teamId?: number, opts?: { includePending?: boolean; includeManual?: boolean }) => {
    const q = new URLSearchParams()
    if (seasonId !== undefined) q.set('season_id', String(seasonId))
    if (teamId !== undefined) q.set('team_id', String(teamId))
    if (opts?.includePending) q.set('include_pending', 'true')
    // mecze ręczne (puchary / Europa spoza hurtowni, ujemne id) — strona drużyny
    if (opts?.includeManual) q.set('include_manual', 'true')
    const qs = q.toString()
    return getJSON<MatchListItem[]>(`/api/matches${qs ? `?${qs}` : ''}`)
  },
  // Obie strony meczu z zawodnikami, którzy wystąpili — pickery ról w „Szansach".
  matchSquads: (matchId: number) => getJSON<ChanceMatchTeam[]>(`/api/matches/${matchId}/squads`),
  // Kadra drużyny w sezonie (kto wystąpił, wg minut) — pickery ról w meczach
  // RĘCZNYCH (puchary), których składu marty nie mają. Drużyna spoza hurtowni
  // (ujemne id) wraca z pustą listą zawodników.
  teamSquad: (teamId: number, seasonId?: number) => {
    const q = new URLSearchParams()
    if (seasonId !== undefined) q.set('season_id', String(seasonId))
    const qs = q.toString()
    return getJSON<ChanceMatchTeam>(`/api/teams/${teamId}/squad${qs ? `?${qs}` : ''}`)
  },
  // Skład rywala pod mapę zawodników (pozycje, metryczka, G+A) — zakres jak dossier.
  playerMapSquad: (teamId: number, opts?: Scope) =>
    getJSON<PlayerMapSquad>(`/api/teams/${teamId}/player-map-squad${scopeQS(opts)}`),
  // PDF mapy zawodników — wszystkie niepuste strony, 16:9, z zapisanego stanu.
  playerMapPdfUrl: (teamId: number, opts?: Scope) => `/api/teams/${teamId}/player-map/pdf${scopeQS(opts)}`,
  // Cały terminarz rozgrywek (jeden wiersz na mecz) — „Szanse" w trybie po kolejce.
  leagueMatches: (competitionId: number, seasonId: number) =>
    getJSON<LeagueMatchItem[]>(
      `/api/matches/league?competition_id=${competitionId}&season_id=${seasonId}`,
    ),
  // Wpięcia mecz↔jednostka motoryki (match_id + unit_number = training_unit.id).
  // Do etykietowania jednostek „Mecz" nazwą przeciwnika PO ID (nie po dacie).
  matchUnitLinks: () => getJSON<{ match_id: number; unit_number: number }[]>('/api/training/match-unit-links'),
  // `period` (1/2) przelicza Warstwę I na tę połowę (marty *_by_period po stronie
  // backendu); `segment` — na tercję sparingu (zdarzenia w oknie minutowym,
  // training.match_segments); brak obu = cały mecz. Wykluczają się (wygrywa period,
  // ale useMatchTactical nigdy nie poda obu naraz). Zob. MatchReport.period / segment.
  report: (matchId: number, period?: 1 | 2 | null, segment?: number | null) =>
    getJSON<MatchReport>(
      `/api/report/${matchId}${period ? `?period=${period}` : segment ? `?segment=${segment}` : ''}`,
    ),
  // Sama sekcja motoryczna (SkillCorner: my vs rywal) — zakładka Motoryka →
  // „Podsumowanie meczowe" bierze ją bez ciągnięcia całego raportu. 404 = mecz
  // bez wierszy SkillCornera (np. poza Ekstraklasą) → sekcja się nie pokazuje.
  matchPhysical: (matchId: number) =>
    getJSON<PhysicalAnalysis>(`/api/matches/${matchId}/physical`),
  pdfUrl: (matchId: number) => `/api/report/${matchId}/pdf`,
  // Raport pomeczowy Legii II (2 liga) — ten sam kształt, tło kafli = 2 liga.
  reservesReport: (matchId: number, period?: 1 | 2 | null) =>
    getJSON<MatchReport>(`/api/legia2/report/${matchId}${period ? `?period=${period}` : ''}`),
  reservesPdfUrl: (matchId: number) => `/api/legia2/report/${matchId}/pdf`,
  // Raport LIVE keyed po SESJI tagowania (nie po meczu StatsBomb).
  // period='h1' → PDF na przerwę (tylko 1. połowa); brak = cały mecz.
  liveSessionPdfUrl: (sessionId: string, period?: 'h1' | 'h2') =>
    `/api/report/live/${sessionId}/pdf${period ? `?period=${period}` : ''}`,
  // `source='players'` zawęża listę do zakresów mających marta sezonowego
  // zawodników (kilka lig zamiast ~200 z team_kpi) — picker w „Zawodnikach".
  leagues: (source?: 'teams' | 'players') =>
    getJSON<LeagueScope[]>(`/api/leagues${source ? `?source=${source}` : ''}`),
  teams: (seasonId?: number, competitionId?: number) => {
    const q = new URLSearchParams()
    if (seasonId !== undefined) q.set('season_id', String(seasonId))
    if (competitionId !== undefined) q.set('competition_id', String(competitionId))
    const qs = q.toString()
    return getJSON<TeamListItem[]>(`/api/teams${qs ? `?${qs}` : ''}`)
  },
  // Strona startowa: tabela z formą + terminarz ligi (każde konto) i liczby naszego
  // zespołu — ostatni mecz, predyktory, xG rywala (tylko z „Raporty meczowe → Mecze").
  homeLeague: () => getJSON<HomeLeague>('/api/home/league'),
  homeTeam: () => getJSON<HomeTeam>('/api/home/team'),
  teamDossier: (teamId: number, opts?: Scope) =>
    getJSON<TeamDossier>(`/api/teams/${teamId}/dossier${scopeQS(opts)}`),
  dossierPdfUrl: (teamId: number, opts?: Scope) =>
    `/api/teams/${teamId}/dossier/pdf${scopeQS(opts)}`,
  // `variant: 'profile'` = wagi osi z karty „Jak gra drużyna" w profilu zawodnika
  // (backend styles.PROFILE_WEIGHTS); dossier woła bez wariantu.
  teamStyle: (teamId: number, opts?: Scope, variant?: 'profile') => {
    const qs = scopeQS(opts)
    const v = variant ? `${qs ? '&' : '?'}variant=${variant}` : ''
    return getJSON<TeamStyleProfile>(`/api/teams/${teamId}/style${qs}${v}`)
  },
  // Ocena motoryczna — wiersze zespół×mecz całej ligi w oknie `fromDate`
  // (przechodzi przez granicę sezonów); metodykę liczy silnik kliencki.
  teamPhysicalAssessment: (teamId: number, opts?: Scope) =>
    getJSON<TeamPhysicalAssessment>(`/api/teams/${teamId}/physical-assessment${scopeQS(opts)}`),
  // Rozbicie meczu na zawodników — klucz to SkillCornerowe sc_match_id z wiersza.
  physicalMatchPlayers: (scMatchId: number) =>
    getJSON<PhysicalMatchPlayers>(`/api/physical/matches/${scMatchId}/players`),
  // Zawodnicy CAŁEJ ligi w sezonie (SkillCorner) — „wykresy formacyjne" Motoryki.
  // Sezon bez feedu zawodniczego oddaje pustą listę (`competition_edition_id: null`).
  leaguePlayerPhysical: (seasonId: number, competitionId?: number) =>
    getJSON<LeaguePlayerPhysical>(
      `/api/physical/league-players?season_id=${seasonId}`
      + (competitionId != null ? `&competition_id=${competitionId}` : ''),
    ),
  teamSetPieces: (teamId: number, opts?: Scope) =>
    getJSON<TeamSetPieces>(`/api/teams/${teamId}/set-pieces${scopeQS(opts)}`),
  teamSetPiecesLeague: (teamId: number, opts?: Scope) =>
    getJSON<LeagueSetPieces>(`/api/teams/${teamId}/set-pieces/league${scopeQS(opts)}`),
  // Arkusz SFG jednego meczu (faza × typ) — automatyzacja „SFG STATYSTYKA MECZ".
  // Bez „szansy": tę front dokłada z ręcznego tagowania.
  matchSetPieces: (matchId: number, teamId?: number) =>
    getJSON<MatchSetPieceSheet>(
      `/api/matches/${matchId}/set-pieces${teamId !== undefined ? `?team_id=${teamId}` : ''}`,
    ),
  // Fazy przejściowe automatu — karta pod skutecznością subfaz z tagowania.
  // `segment` = tercja sparingu (okno minutowe po stronie backendu).
  matchTransitions: (matchId: number, teamId?: number, segment?: number | null) => {
    const q = new URLSearchParams()
    if (teamId !== undefined) q.set('team_id', String(teamId))
    if (segment) q.set('segment', String(segment))
    const qs = q.toString()
    return getJSON<MatchTransitionSheet>(`/api/matches/${matchId}/transitions${qs ? `?${qs}` : ''}`)
  },
  // Subfazy ATAK / OBRONA automatu — skuteczność Warstwy II i rozkłady kart
  // Atak/Obrona Warstwy III. `period` (połowa) działa tu wprost — model ma
  // wymiar okresu, inaczej niż automaty SFG/przejść liczące cały mecz.
  matchSubphases: (
    matchId: number, teamId?: number, period?: number | null, segment?: number | null,
  ) => {
    const q = new URLSearchParams()
    if (teamId !== undefined) q.set('team_id', String(teamId))
    if (period) q.set('period', String(period))
    if (segment) q.set('segment', String(segment))
    const qs = q.toString()
    return getJSON<MatchSubphaseSheet>(`/api/matches/${matchId}/subphases${qs ? `?${qs}` : ''}`)
  },
  // Kumulatywny arkusz sezonu — wiersz na mecz, sumy liczy klient. `matchIds`
  // (filtr zakładki SFG: mecze oficjalne sezonu, zakres dat) wygrywa z zakresem.
  teamSetPiecesSeason: (teamId: number, opts?: Scope & { matchIds?: number[] }) => {
    if (opts?.matchIds?.length) {
      return getJSON<SeasonSetPieceSheet>(
        `/api/teams/${teamId}/set-pieces/season?${matchIdsQS(opts.matchIds)}`,
      )
    }
    return getJSON<SeasonSetPieceSheet>(`/api/teams/${teamId}/set-pieces/season${scopeQS(opts)}`)
  },
  // Gole i szanse po SFG (atak / obrona) z podanych meczów — szansa z tagowania
  // sztabu, gole i strzały dopięte ze StatsBomba.
  teamSfgOutcomes: (teamId: number, matchIds: number[]) =>
    getJSON<SfgOutcomes>(`/api/teams/${teamId}/set-pieces/outcomes?${matchIdsQS(matchIds)}`),
  // 1. i 2. kontakt po rożnych, wolnych i autach z 3T — dogranie po dograniu.
  teamSfgContacts: (teamId: number, matchIds: number[]) =>
    getJSON<SfgContacts>(`/api/teams/${teamId}/set-pieces/contacts?${matchIdsQS(matchIds)}`),
  // Bez zakresu — bieżący sezon ligowy naszej drużyny (domyślka backendu).
  season: (opts?: Scope) => getJSON<SeasonRankings>(`/api/season${scopeQS(opts)}`),
  // Ranking naszych meczów ligowych po kaflach Warstwy I. Bez parametrów —
  // Ekstraklasa w oknie benchmarku raportu pomeczowego (te same percentyle).
  // `competitionId` — ta sama populacja dla INNEJ ligi (tabela „Ranking ligi"
  // w dossier przeciwnika); nasze mecze wracają wtedy puste, wiersze ligi pełne.
  matchRanking: (competitionId?: number) =>
    getJSON<MatchRanking>(`/api/matches/ranking${competitionId !== undefined ? `?competition_id=${competitionId}` : ''}`),
  // Chudy wycinek raportu (skład, strzały, wejścia w PK, pojedynki, dośrodkowania,
  // fazy gry) pod sumy „Podsumowania okresowego" — ~2 s zamiast ~9 s pełnego raportu.
  // `teamId` przestawia perspektywę „my / rywale" na inną drużynę meczu (dossier
  // przeciwnika sumuje jego mecze z jego punktu widzenia); bez = nasza drużyna.
  matchSummary: (matchId: number, teamId?: number) =>
    getJSON<MatchSummary>(`/api/matches/${matchId}/summary${teamId !== undefined ? `?team_id=${teamId}` : ''}`),
  periodSummaryPdfUrl: (state: string) => `/api/matches/summary/pdf?state=${encodeURIComponent(state)}`,
  players: (opts?: {
    seasonId?: number
    competitionId?: number
    positionLine?: string
    positionGroup?: string
  }) => {
    const q = new URLSearchParams()
    if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
    if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
    if (opts?.positionLine) q.set('position_line', opts.positionLine)
    if (opts?.positionGroup) q.set('position_group', opts.positionGroup)
    const qs = q.toString()
    return getJSON<PlayerListEntry[]>(`/api/players${qs ? `?${qs}` : ''}`)
  },
  playerCohort: (positionGroup: string, opts?: { competitionId?: number; seasonId?: number }) => {
    const q = new URLSearchParams()
    q.set('position_group', positionGroup)
    if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
    if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
    return getJSON<CohortMetricRow[]>(`/api/players/cohort?${q.toString()}`)
  },
  /** Profil ligi (Skauting): sumy sezonu wszystkich zawodników ligi. */
  leagueProfilePlayers: (competitionId: number, seasonId: number) =>
    getJSON<LeaguePlayerTotals[]>(`/api/league-profile/players?competition_id=${competitionId}&season_id=${seasonId}`),
  strengthLeague: (competitionId: number, seasonId: number, scale: 'same' | 'current' = 'same') =>
    getJSON<StrengthLeague>(`/api/strength/league?competition_id=${competitionId}&season_id=${seasonId}&scale=${scale}`),
  // Posiadanie i PPDA drużyn ligi w sezonie — karta „Jak gra drużyna" w profilu zawodnika.
  leagueTeamPlay: (competitionId: number, seasonId: number) =>
    getJSON<LeagueTeamPlay>(`/api/league-profile/team-play?competition_id=${competitionId}&season_id=${seasonId}`),
  strengthLeagues: (seasonYear: number, scale: 'same' | 'current' = 'current') =>
    getJSON<StrengthLeagues>(`/api/strength/leagues?season_year=${seasonYear}&scale=${scale}`),
  leaguePhysicalLeagues: (year: number) =>
    getJSON<LeaguePhysicalLeagues>(`/api/league-profile/physical/leagues?year=${year}`),
  leaguePhysicalPlayers: (year: number, editionId: number) =>
    getJSON<LeaguePhysicalPlayers>(`/api/league-profile/physical/players?year=${year}&edition_id=${editionId}`),
  /** Wyszukiwarka porównania: fragment nazwiska (≥ 3 znaki) albo wszystkie zakresy jednego zawodnika. */
  playerSearch: (opts: { q?: string; playerId?: number }) => {
    const q = new URLSearchParams()
    if (opts.q) q.set('q', opts.q)
    if (opts.playerId !== undefined) q.set('player_id', String(opts.playerId))
    return getJSON<PlayerSearchHit[]>(`/api/players/search?${q.toString()}`)
  },
  /** `specs` = `player_id:competition_id:season_id[:pozycja]`; `ref` = kohorta odniesienia. */
  playerCompare: (specs: string[], ref?: { competitionId: number; seasonId: number }, vs?: string) => {
    const q = new URLSearchParams()
    for (const s of specs) q.append('p', s)
    if (ref) {
      q.set('ref_competition_id', String(ref.competitionId))
      q.set('ref_season_id', String(ref.seasonId))
    }
    if (vs) q.set('vs', vs)
    return getJSON<PlayerCompareData>(`/api/players/compare?${q.toString()}`)
  },
  // Profil → „Historia": kariera z Transfermarktu + sezony StatsBomba (bez zakresu ligi).
  playerHistory: (playerId: number) => getJSON<PlayerHistory>(`/api/players/${playerId}/history`),
  // Profil bramkarza: mecz po meczu PSxG ±, strzały w świetle bramki, dystrybucja.
  playerGoalkeeper: (playerId: number, competitionId: number, seasonId: number) =>
    getJSON<PlayerGoalkeeperDetail>(`/api/players/${playerId}/goalkeeper?competition_id=${competitionId}&season_id=${seasonId}`),
  /** Profil roli (radar poziomu + archetypy) — na razie tylko kohorta CF. */
  playerRoleProfile: (playerId: number, competitionId: number, seasonId: number, pos: string) =>
    getJSON<RoleProfile>(`/api/players/${playerId}/role-profile?competition_id=${competitionId}&season_id=${seasonId}&pos=${encodeURIComponent(pos)}`),
  // Kontuzje — tylko z uprawnieniem `kontuzje` (useInjuriesAccess; bez niego NIE pytać).
  playerInjuries: (playerId: number) => getJSON<PlayerInjury[]>(`/api/players/${playerId}/injuries`),
  /** `vs` = „porównaj z”: kohorta, na tle której liczone są percentyle profilu. */
  playerProfile: (playerId: number, pos?: string, opts?: Scope, vs?: string) => {
    const q = new URLSearchParams()
    if (pos) q.set('pos', pos)
    if (vs) q.set('vs', vs)
    if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
    if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
    const qs = q.toString()
    return getJSON<PlayerProfile>(`/api/players/${playerId}${qs ? `?${qs}` : ''}`)
  },
}
